import { Linkedin, Facebook, MapPin, Mail } from "lucide-react";
import { useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { useBranding } from "@/components/BrandingProvider";
import { useTheme } from "@/components/ThemeProvider";
import { scrollToSection } from "@/lib/section-nav";

const quickLinks = [
  { href: "/", label: "footer.home" },
  { href: "#use-cases", label: "footer.caseStudies" },
  { href: "#pricing", label: "nav.pricing" },
  { href: "/blog", label: "nav.blog" },
  { href: "#capabilities", label: "footer.about" },
  { href: "#contact", label: "nav.contact" },
];

const legalLinks = [
  { href: "https://xenonlabs.ai/terms.html", label: "footer.terms" },
  { href: "https://xenonlabs.ai/privacy.html", label: "footer.privacy" },
  { href: "https://xenonlabs.ai/cookies.html", label: "footer.cookies" },
];

export function Footer() {
  const { branding } = useBranding();
  const { theme } = useTheme();
  const { t } = useTranslation("landing");

  const [currentLocation, setLocation] = useLocation();

  const handleAnchorClick = (href: string) => {
    if (href.startsWith("#")) {
      if (currentLocation !== "/") {
        // Navigate to landing page with hash; it scrolls on mount.
        setLocation("/" + href);
      } else {
        scrollToSection(href);
      }
    } else if (href === "/") {
      if (currentLocation !== "/") {
        setLocation("/");
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } else if (href.startsWith("/")) {
      setLocation(href);
    }
  };

  return (
    <footer className="relative bg-white text-gray-700 overflow-hidden border-t border-gray-200" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Main Footer Grid - 3 columns */}
        <div className="py-12 sm:py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Column 1: Logo, Description, Social */}
          <div className="space-y-6">
            <a
              href="https://xenonlabs.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
              data-testid="link-footer-xenonlabs"
            >
              <img
                src={theme === "dark" ? "/images/branding/voiceprologowhite.svg" : "/images/branding/voiceprologo.svg"}
                alt="VoicePro - A product of XenonLabs.ai"
                className="h-10 sm:h-12 md:h-14 lg:h-16 w-auto object-contain"
                data-testid="img-footer-logo"
              />
            </a>
            <p className="text-gray-600 leading-relaxed text-sm">
              {t("footer.by")}{" "}
              <a
                href="https://xenonlabs.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-blue-600 hover:text-blue-700 transition-colors"
              >
                XenonLabs.ai
              </a>{" "}
              {t("footer.blurb")}
            </p>

            {/* Social Icons */}
            <div className="flex items-center gap-3 pt-1">
              <a
                href={branding.social_linkedin_url || "https://www.linkedin.com/company/xenonlabs-ai"}
                target="_blank"
                rel="noopener noreferrer"
                className="h-10 w-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors group"
                data-testid="link-footer-linkedin"
                aria-label="LinkedIn"
              >
                <Linkedin className="h-5 w-5 text-gray-600 group-hover:text-blue-600 transition-colors" />
              </a>
              <a
                href="https://www.facebook.com/xenonlabsai"
                target="_blank"
                rel="noopener noreferrer"
                className="h-10 w-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors group"
                data-testid="link-footer-facebook"
                aria-label="Facebook"
              >
                <Facebook className="h-5 w-5 text-gray-600 group-hover:text-blue-600 transition-colors" />
              </a>
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div className="space-y-4">
            <h3 className="text-base font-semibold text-gray-900 pb-1 border-b-2 border-blue-600 inline-block">
              {t("footer.quickLinks")}
            </h3>
            <ul className="space-y-3 pt-2">
              {quickLinks.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    onClick={(e) => {
                      if (link.href.startsWith("#") || link.href === "/" || link.href.startsWith("/")) {
                        e.preventDefault();
                        handleAnchorClick(link.href);
                      }
                    }}
                    className="text-gray-600 hover:text-blue-600 transition-colors text-sm"
                    data-testid={`link-footer-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {t(link.label)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Contact Us */}
          <div className="space-y-4">
            <h3 className="text-base font-semibold text-gray-900 pb-1 border-b-2 border-blue-600 inline-block">
              {t("footer.contactUs")}
            </h3>
            <ul className="space-y-5 pt-2">
              {/* Australia */}
              <li className="flex items-start gap-2.5">
                <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-blue-600" />
                <div>
                  <p className="text-sm font-semibold text-gray-900">{t("footer.australia")}</p>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    125-131 Harrow Rd, Kogarah,<br />
                    NSW 2217, Sydney
                  </p>
                </div>
              </li>
              {/* India */}
              <li className="flex items-start gap-2.5">
                <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-blue-600" />
                <div>
                  <p className="text-sm font-semibold text-gray-900">{t("footer.india")}</p>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    565, 9th Cross Rd, 7th Main Rd, Sarakki, 3rd Phase,<br />
                    J.P. Nagar, Bengaluru &ndash; 560078,<br />
                    Bangalore South
                  </p>
                </div>
              </li>
              {/* Email */}
              <li className="flex items-center gap-2.5">
                <Mail className="h-4 w-4 shrink-0 text-blue-600" />
                <a
                  href="mailto:connect@xenonlabs.ai"
                  className="text-sm text-gray-600 hover:text-blue-600 transition-colors"
                  data-testid="link-footer-email"
                >
                  connect@xenonlabs.ai
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-5 border-t border-gray-200 flex flex-col items-center justify-center gap-4 text-center">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span data-testid="text-copyright">
              &copy; {new Date().getFullYear()}{" "}
              <a
                href="https://xenonlabs.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-blue-600 transition-colors"
              >
                XenonLabs
              </a>. {t("footer.rights")}
            </span>
            <span className="text-gray-300">|</span>
            <a
              href="https://xenonlabs.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-700 font-medium transition-colors"
            >
              AU | IN
            </a>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-sm">
            {legalLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-blue-600 font-medium transition-colors"
                data-testid={`link-footer-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {t(link.label)}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
